import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { urlForImage } from "../lib/sanityImageUrl";
import { useLanguage } from "../hooks/useLanguage";
import { buildBlogPath } from "../utils/languageUtils";
import "./SearchResults.css";

interface SanityImage {
  asset: { _ref: string };
}

interface Category {
  title: string;
}

interface Post {
  _id: string;
  title: string;
  slug: { current: string };
  mainImage?: SanityImage;
  categories?: Category[];
  excerpt?: string;
  publishedAt: string;
  author?: string;
  claps?: number;
}

interface SearchResultsProps {
  results: Post[];
  isVisible: boolean;
  onClose: () => void;
}

const SearchResults = ({ results, isVisible, onClose }: SearchResultsProps) => {
  const { t } = useTranslation(["blog"]);
  const { currentLang } = useLanguage();

  useEffect(() => {
    document.body.style.overflow = isVisible ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div className='search-overlay'>
      <div className='search-backdrop' onClick={onClose} />
      <div className='search-modal'>
        <button
          className='search-close'
          onClick={onClose}
          aria-label={t("chrome.searchClose", "Close search")}
        >
          <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <path d='M6 18L18 6M6 6l12 12' />
          </svg>
        </button>

        <div className='search-content'>
          {results.length === 0 ? (
            <p className='search-no-results'>
              {t("chrome.searchNoResults", "No results found")}
            </p>
          ) : (
            <div className='search-grid'>
              {results.map((post) => (
                <Link
                  key={post._id}
                  to={buildBlogPath(currentLang, post.slug.current)}
                  className='search-card'
                  onClick={onClose}
                >
                  <div className='search-card-inner'>
                    {post.mainImage?.asset && (
                      <div className='search-card-image'>
                        <img
                          src={urlForImage(post.mainImage).width(400).height(225).url()}
                          alt={post.title}
                        />
                      </div>
                    )}
                    <div className='search-card-content'>
                      {post.categories?.[0] && (
                        <div className='search-category'>{post.categories[0].title}</div>
                      )}
                      <h3 className='search-title'>{post.title}</h3>
                      {post.excerpt && <p className='search-excerpt'>{post.excerpt}</p>}
                      <div className='search-meta'>
                        <time className='search-date'>
                          {new Date(post.publishedAt).toLocaleDateString(currentLang)}
                        </time>
                        {post.author && (
                          <span className='search-author'>
                            {t("author")} {post.author}
                          </span>
                        )}
                        {post.claps && post.claps > 0 && (
                          <span className='search-claps'>{post.claps}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResults;
